import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getIcon, formatPlatformName } from "../../constants/constants";
import { Icons } from "../../constants/icon-library";
import { useDispatch, useSelector } from "react-redux";
import { getIntegration } from "../../features/meeting-integration/meetingIntegrationSlice";
import { upsertIntegrationSetting } from "../../features/integration-setting/integrationSettingSlice";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getApiKeySteps } from "./Integrations.utils";
import { toast } from "react-toastify";
import { INTEGRATION_ENUM } from "../../constants/constants";

function SetUp() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showKey, setShowKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useSelector((state) => state.user);
  const { selectedIntegration } = useSelector(
    (state) => state.meetingIntegrations,
  );
  const integrationName = selectedIntegration?.name?.toLowerCase();
  const iconSrc = getIcon(integrationName);
  const steps = getApiKeySteps[integrationName] || [];

  useEffect(() => {
    if (id) {
      dispatch(getIntegration(id));
    }
  }, [id, dispatch]);

  const formik = useFormik({
    initialValues: {
      apiKey: "",
      subDomain: "",
    },
    validationSchema: Yup.object({
      apiKey: Yup.string().required("API key is required"),
      subDomain:
        integrationName === INTEGRATION_ENUM.FELLOW_AI
          ? Yup.string().required("Subdomain is required")
          : Yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        await dispatch(
          upsertIntegrationSetting({
            integrationId: selectedIntegration?.uuid,
            accessToken: values.apiKey,
            subDomain: values?.subDomain,
            isInstallationCompleted: true,
          }),
        );

        toast.success("Integration connected successfully");
        navigate(
          user?.isOnboardingCompleted
            ? "/integrations"
            : "/onboarding/note-taker/integrations",
        );
      } catch (err) {
        console.error("Save failed:", err);
        toast.error(
          err?.message || err?.error || "Failed to connect integration",
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <div className="container my-6">
      <div className="card border-0 shadow-lg" style={{ marginTop: "60px" }}>
        <div className="card-body p-4 p-md-5">
          <div className="d-flex align-items-center flex-grow-1 w-100 w-md-auto mb-4">
            <div
              className="rounded-circle bg-gradient d-flex justify-content-center align-items-center me-3 flex-shrink-0"
              style={{
                width: "60px",
                height: "60px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 4px 15px rgba(102,126,234,0.4)",
              }}
            >
              <img
                src={iconSrc}
                alt={integrationName}
                style={{ width: "36px", height: "36px" }}
              />
            </div>

            <div>
              <h3 className="mb-1 fw-bold">
                {formatPlatformName(integrationName)} Setup
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                Connect your meeting platform in a few simple steps
              </p>
            </div>
          </div>

          <div
            className="card border border-translucent shadow-sm mb-5"
            style={{
              background: "rgba(255,255,255,0.05)",
              backdropFilter: "blur(10px)",
              borderRadius: "14px",
            }}
          >
            <div className="card-body p-4">
              <h5 className="fw-bold mb-4">Follow these steps</h5>

              <div className="d-flex flex-column gap-2">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="d-flex align-items-center gap-2 px-3 py-2 rounded"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: "24px",
                        height: "24px",
                        fontSize: "12px",
                        fontWeight: "600",
                        background: "rgba(102,126,234,0.2)",
                        color: "#667eea",
                      }}
                    >
                      {index + 1}
                    </div>

                    <div style={{ fontSize: "0.88rem" }}>{step}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className="card border border-translucent shadow-sm"
            style={{
              background: "rgba(255,255,255,0.05)",
              backdropFilter: "blur(10px)",
              borderRadius: "14px",
            }}
          >
            <div className="card-body p-4">
              <h5 className="fw-bold mb-2">
                Enter API Key
                {integrationName === INTEGRATION_ENUM.FELLOW_AI
                  ? " and Sub Domain"
                  : ""}
              </h5>

              <p
                className="text-muted mb-4"
                style={{ fontSize: "0.9rem", lineHeight: "1.5" }}
              >
                Enter your API key or access token{" "}
                {integrationName === INTEGRATION_ENUM.FELLOW_AI
                  ? "and Sub Domain "
                  : ""}
                to import all your meetings from your AI meeting platform and
                automatically extract tasks, summaries, and insights.
              </p>

              <form onSubmit={formik.handleSubmit}>
                <div
                  className="d-flex align-items-center mb-2 px-3"
                  style={{
                    height: "52px",
                    borderRadius: "10px",
                    border:
                      formik.touched.apiKey && formik.errors.apiKey
                        ? "1px solid rgba(241, 21, 21, 0.47)"
                        : "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  <i
                    className={`wb ${Icons("key") || Icons("edit")} me-2 text-muted`}
                    style={{ fontSize: "16px" }}
                  />

                  <input
                    type={showKey ? "text" : "password"}
                    name="apiKey"
                    className="form-control border-0 bg-transparent shadow-none px-2"
                    placeholder="Enter API Key or Access Token"
                    value={formik.values.apiKey}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{ flex: 1 }}
                  />

                  <i
                    className={`wb ${
                      showKey
                        ? Icons("conditionalOpenModal") || Icons("cross")
                        : Icons("system_hide_component")
                    } text-muted`}
                    style={{ cursor: "pointer", fontSize: "16px" }}
                    onClick={() => setShowKey(!showKey)}
                  />
                </div>
                {formik.touched.apiKey && formik.errors.apiKey && (
                  <div
                    className="text-danger mb-3"
                    style={{ fontSize: "0.8rem" }}
                  >
                    {formik.errors.apiKey}
                  </div>
                )}

                {integrationName === INTEGRATION_ENUM.FELLOW_AI && (
                  <>
                    <div
                      className="d-flex align-items-center mb-2 px-3 mt-4"
                      style={{
                        height: "52px",
                        borderRadius: "10px",
                        border:
                          formik.touched.subDomain && formik.errors.subDomain
                            ? "1px solid rgba(241, 21, 21, 0.47)"
                            : "1px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      <i
                        className={`wb ${"webconnect-globe" || Icons("edit")} me-2 text-muted`}
                        style={{ fontSize: "16px" }}
                      />

                      <input
                        type="text"
                        name="subDomain"
                        className="form-control border-0 bg-transparent shadow-none px-2"
                        placeholder="Enter sub domain"
                        value={formik.values.subDomain}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        style={{ flex: 1 }}
                      />
                      <span
                        className="text-muted"
                        style={{ whiteSpace: "nowrap", fontSize: "0.9rem" }}
                      >
                        .fellow.app
                      </span>
                    </div>

                    {formik.touched.subDomain && formik.errors.subDomain && (
                      <div
                        className="text-danger mb-3"
                        style={{ fontSize: "0.8rem" }}
                      >
                        {formik.errors.subDomain}
                      </div>
                    )}
                  </>
                )}

                <div className="d-flex justify-content-between align-items-center mt-5">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() =>
                      navigate(
                        user?.isOnboardingCompleted
                          ? "/integrations"
                          : "/onboarding/note-taker/integrations",
                      )
                    }
                    disabled={isLoading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary px-4 d-flex align-items-center gap-2"
                    disabled={
                      !formik.isValid || !formik.values.apiKey || isLoading
                    }
                  >
                    {isLoading && (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                      ></span>
                    )}
                    {isLoading ? "Saving..." : "Save & Continue"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SetUp;
